import {
  buildPageContextBlock,
  composeAgentMessage,
  type ContextBlockItem,
  type ContextBudget,
  type PageContextContributor,
} from '@logz-pkg/models';

// PURE collector for the generic page-context seam (ORIONIQ-1384): a route-resolved contributor + a budget in,
// the `context.systemReminder` context blocks out. No React, no observables — it joins `chat-mode.ts` as shared
// general-agent logic both OrionIQ consumers (and, later, Artemis) can build on.

// Generous by design: it exists to stop a runaway contributor, not to trim any page we ship today (Explore's
// richest state is ~10 rows). Layer-2 sets are bounded by their own adapter caps (TOP_N_OPS / MAX_SPANS), so the
// attached cap counts WHOLE datasets — an item is never sliced into a silently-incomplete one.
export const DEFAULT_CONTEXT_BUDGET: ContextBudget = { maxDirectItems: 40, maxAttachedItems: 3 };

// First occurrence of a key wins (a contributor composing several sources can't emit the same row twice), and
// `secret` values never reach the wire.
export const selectContextItems = (items: ContextBlockItem[], budget: ContextBudget): ContextBlockItem[] => {
  const seen = new Set<string>();
  const selected: ContextBlockItem[] = [];

  for (const item of items) {
    if (item.sensitivity === 'secret' || seen.has(item.key)) continue;

    seen.add(item.key);
    selected.push(item);

    if (selected.length >= budget.maxDirectItems) break;
  }

  return selected;
};

/**
 * Whether the contributor has anything to send — drives the Context panel's on/off dot.
 * Attached datasets count on their own: a page whose every `<page_context>` row is unchecked can still be sending
 * user-attached items, and the dot has to agree with what the panel shows as selected. Short-circuits, so the
 * common case never pays for serializing the attachments.
 *
 * `getPageIdentity` is deliberately NOT counted (ORIONIQ-1168). The dot means "there is context you control", and
 * the identity is neither shown in the panel nor switchable — counting it would light the dot on every page over a
 * panel with nothing in it.
 */
export const hasPageContext = (contributor?: PageContextContributor): boolean =>
  Boolean(contributor && (contributor.getDirectContext().length || contributor.getAttachedBlocks?.()));

/**
 * The FE-assembled context blocks for `/oiq-bot` `context.systemReminder`, in wire order:
 * `<page_context>` → `<data_sources>` → the contributor's `<attached_*>` blocks.
 *
 * Returns `undefined` only when there is NOTHING to send — no page identity, no enabled row and no attached
 * dataset — so the request omits the field entirely; a scope directive on its own describes nothing. The
 * `<response_mode>` directive is prepended by the caller.
 */
export const buildSystemReminderBlocks = ({
  contributor,
  budget = DEFAULT_CONTEXT_BUDGET,
}: {
  contributor?: PageContextContributor;
  budget?: ContextBudget;
}): string | undefined => {
  if (!contributor) return undefined;

  const items = selectContextItems(contributor.getDirectContext(), budget);
  const attachedBlocks = contributor.getAttachedBlocks?.();
  // The frame, not state: sent whenever the contributor claims the route, whatever the user left checked
  // (ORIONIQ-1168). See `PageContextContributor.getPageIdentity`.
  const identity = contributor.getPageIdentity?.();

  if (!identity && !items.length && !attachedBlocks) return undefined;

  const sections = [
    // `<page_context>` is emitted for the identity ALONE, so the agent still knows which page it is answering from
    // on a page with no rows (K8s 360, the Integrations hub) or with every row switched off.
    ...(identity || items.length ? [buildPageContextBlock({ intro: identity, items })] : []),
    // `<data_sources>` stays gated on the ROWS: it speaks about the accounts `<page_context>` lists, so alongside
    // an identity-only block it would assert an account scope that names nothing.
    ...(items.length ? [contributor.getDataSourcesBlock?.()] : []),
    attachedBlocks,
  ].filter((section): section is string => Boolean(section));

  return composeAgentMessage(...sections) || undefined;
};
